import { z } from 'zod';
import { CLIENT_EVENT_SOURCES } from './client-events.js';
import { PackageName, Platform, SessionKind, TeamRole, Ulid } from './common.js';
import { FeatureKey, FeatureTier, Plan, Tier } from './features.js';
import { ClaudeCodeSandboxSize, SandboxMinutes, SandboxSize } from './playgrounds.js';
/**
 * The admin console's contract (`docs/admin-plan.md`).
 *
 * ## This module is the one place plans are spoken about out loud
 *
 * Everywhere else in this package, a plan is invisible: `Team.features` carries the keys that are
 * **on** and nothing about the ones that are off, so a free account's response is shaped exactly
 * like one from a build of BoardFlight where the paid feature was never written. That is a product
 * requirement, not an implementation detail, and `apps/api/test/features.test.ts` holds it up by
 * asserting that no response body anywhere contains the string `"plan"`.
 *
 * The schemas below deliberately break that rule, because the console is the *operator's* view and
 * the operator is the person who sets the plans and puts people in the alpha. Nothing here is ever
 * served to a client that is not already authenticated as a project admin — `requireAdmin` in
 * `@boardflight/worker-core/admin` is the only door — and no type in this file is referenced by any
 * other module in this package. If one of these shapes turns up in an ordinary response, that is
 * the bug the invisibility tests exist to catch.
 *
 * ## What the console may write is a closed list
 *
 * `feature_tiers`, `users.tier`, `teams.plan`, `teams.playground_seats`, `teams.sandbox_size`,
 * `teams.claudecode_sandbox_size`, `teams.sandbox_minutes`, `analytics_exclusions`, and
 * `apps.package_name` and the store columns beside it. That is all, and every one of them is a
 * column an operator edits with `UPDATE` today — the console is a second hand on the same columns,
 * and `resolveFeature` remains the only thing that decides what a tier or a plan reaches (§D5, §D6).
 *
 * `feature_tiers` is how a feature is released: off, then the alpha, then the beta, then a paid team
 * or everybody. It is the one write here that changes what **everybody** gets, so the screen asks
 * first. What the console does not do is give anybody one feature on its own: there is no
 * per-feature access to give (migration 0087).
 *
 * The team's sandbox numbers and the package id are the ones that are not about plans at all: a
 * team's container sizes and session length are the other half of its seat count, and a game's
 * package id is the §4 publishing boundary. Each is described where it is defined below, and each
 * was a change to `docs/admin-plan.md` §D5 before it was a change to a route.
 */
/* ------------------------------------------------------------------------------ requests */
/** A team's plan (`teams.plan`). People pay per team, so this is the only plan there is. */
export const SetPlanRequest = z.object({ plan: Plan });
/**
 * A person's tier (`users.tier`, migration 0087).
 *
 * All three values, though a person can choose two of them themselves (`PATCH /v1/me`): the console
 * is the only way into the alpha, and an operator taking somebody out of it has to say where to —
 * back to the beta they may have joined, or out of both. `Tier` mirrors the column's CHECK, so a
 * fourth word is a 400 that names the three rather than a constraint failure surfacing as a 500.
 */
export const SetTierRequest = z.object({ tier: Tier });
/** Move one feature along its release: `PUT /v1/admin/features/:key`. */
export const SetFeatureTierRequest = z.object({ tier: FeatureTier });
/**
 * `?tier=` on `GET /v1/admin/accounts`: the accounts in exactly this tier. Not `free`, which is
 * nearly everybody and so is the list with no filter at all; anything outside these is no opinion.
 */
export const ADMIN_ACCOUNT_TIERS = ['beta', 'alpha'];
/**
 * How many sandboxes a team may run at once (`teams.playground_seats`, migration 0017).
 *
 * The bounds mirror the column's CHECK exactly, so a bad number is a 400 that names the range
 * rather than a D1 constraint failure surfacing as a 500. Zero is a legitimate value and means
 * *no sandboxes*, which is a different statement from taking the `playground` feature away — a
 * number and a switch, as migration 0017 puts it.
 */
export const SetSeatsRequest = z.object({ seats: z.number().int().min(0).max(50) });
/**
 * How big a machine this team's sandboxes get (`teams.sandbox_size`, migration 0029).
 *
 * The other half of the seat count: seats bound how many at once, this bounds how big each one
 * is. Both are a number and a switch an operator sets and a team cannot, and both are read at the
 * moment a session starts rather than stored on the playground — so a size changed while somebody
 * is working applies to their next sandbox and never resizes the one they are in, which is not
 * something E2B can do anyway.
 *
 * `SandboxSize` mirrors the column's CHECK exactly, so a value outside it is a 400 that names the
 * four rather than a D1 constraint failure surfacing as a 500.
 */
export const SetSandboxSizeRequest = z.object({ size: SandboxSize });
/**
 * How big a machine this team's Claude Code sandboxes get (`teams.claudecode_sandbox_size`,
 * migration 0047).
 *
 * The Claude Code image is its own template family, published only at the sizes with the memory
 * Claude Code asks for, so its size is its own column rather than `sandbox_size` lifted to fit.
 * `ClaudeCodeSandboxSize` mirrors the column's CHECK; read at a session's start, like the other.
 */
export const SetClaudeCodeSandboxSizeRequest = z.object({ size: ClaudeCodeSandboxSize });
/**
 * How long one of this team's sandboxes may last, in minutes (`teams.sandbox_minutes`, migration
 * 0047) — every sandbox, whichever agent it runs.
 *
 * Read when a session starts and fixed for that sandbox's life, so a change applies to the team's
 * next sandbox. `SandboxMinutes` mirrors the column's CHECK.
 */
export const SetSandboxMinutesRequest = z.object({ minutes: SandboxMinutes });
/**
 * Rename the package id of one game (`apps.package_name`, §D5 row 6).
 *
 * The heaviest write in this console, and the only one that is not about what somebody *may* do
 * but about what one of their games **is**. A package id is an installation identity: it is what
 * an APK's manifest must declare at finalize, what a Board fights over when two apps claim one,
 * and the thing §4's prefix ownership is ownership *of*. Renaming is therefore an operator's
 * repair — a typo caught after the first build, a game moved between prefixes its team owns — and
 * not a setting a team is offered, which is why `PATCH /v1/apps/:id` still cannot touch it.
 *
 * The note is required, for the same reason the reveal's and the sandbox kill's are: this is one
 * of the few acts in the system whose *why* is recoverable from nothing else. What it cannot do
 * is rewrite history — every build already published keeps the id its bytes actually declare, and
 * the route says how many of them there are rather than leaving that to be discovered on a device.
 */
export const SetPackageNameRequest = z.object({
    packageName: PackageName,
    note: z.string().trim().min(1).max(500),
});
/**
 * Excuse one game from the store's readiness checklist, or put it back (`docs/admin-plan.md`
 * §D5.7).
 *
 * `waived` is a plain boolean, not a three-state `nullable`: there is nothing here for a third value
 * to mean. A game is excused or it is not, and there is nothing underneath for "not set" to fall
 * through to.
 *
 * The note is required, as the rename's, the reveal's and the sandbox kill's are. This one earns it
 * twice over: the checklist exists because the storefront is the one surface a stranger sees, so
 * every waiver is a deliberate exception to a rule that is right the rest of the time, and *which*
 * exception is recoverable from nothing else. Required on clearing too — putting the gate back is
 * as much a decision as taking it away.
 */
export const SetStoreReadinessWaiverRequest = z.object({
    waived: z.boolean(),
    note: z.string().trim().min(1).max(500),
});
/* ----------------------------------------------------------------------------- responses */
/**
 * One feature, as one half of the question answers it.
 *
 * What somebody has is decided by two values — the plan of the team they act in and their own tier
 * — and a page about an account or about a team knows only one of them. So each row says what that
 * half settles and no more: `true` or `false` where the answer is the same whatever the other half
 * is, and `null` where it is not.
 */
export const AdminFeatureState = z.object({
    key: FeatureKey,
    /** `FEATURES[key].tier` — who reaches it. Moved in code, never here. */
    tier: FeatureTier,
    /** The registry's one-line summary. */
    summary: z.string(),
    /** `FEATURES[key].effect` — what having this does, and what not having it leaves alone. */
    effect: z.string(),
    /**
     * `resolveFeature`'s answer for **this half alone**, or `null` when it turns on the other one.
     *
     * On an account's page a `paid` feature is `null`: it depends on the team the person acts in, and
     * that answer is on the account's `teams[].features`, the pair resolved. On a team's page a
     * `beta` or `alpha` feature is `null`: it depends on the person. A `free` feature is `true` on
     * both. The null is found by asking the resolver over every value the missing half can take, not
     * by reading the tier, so the console cannot disagree with the Worker about which half matters.
     */
    enabled: z.boolean().nullable(),
});
export const AdminAccount = z.object({
    id: Ulid,
    email: z.string(),
    username: z.string(),
    displayName: z.string().nullable(),
    /** How early this account gets new features. The plan is the team's, and is on `AdminAccountTeam`. */
    tier: Tier,
    /** A project admin. Not `team_members.role = 'admin'`, which is per team and appears below. */
    admin: z.boolean(),
    disabled: z.boolean(),
    /** On the analytics exclusion list (§3.3): no analytics, anywhere, counts this account. */
    analyticsExcluded: z.boolean(),
    createdAt: z.number().int(),
    /**
     * When anything signed in as this account was last used: the newest sign-in or token refresh of
     * any of its sessions — the website, the desktop app, the CLI and plugins (§3.5). Not when it
     * last played: no play names an account. Null for an account with no session left.
     */
    lastActiveAt: z.number().int().nullable(),
    /** Teams it is a member of. None is a player: somebody who redeemed a code or browsed the library. */
    teams: z.number().int(),
    /** Has uploaded at least one build: a creator who shipped, not only one who signed up. */
    shipped: z.boolean(),
});
/** `?order=` on `GET /v1/admin/accounts`: newest signups first, or most recently active first. */
export const ADMIN_ACCOUNT_ORDERS = ['joined', 'active'];
/** Why and since when an account is on the analytics exclusion list, and who put it there. */
export const AdminAnalyticsExclusion = z.object({
    note: z.string(),
    /** The operator's handle. Null once that account is gone — the row outlives them. */
    createdBy: z.string().nullable(),
    createdAt: z.number().int(),
});
/**
 * What `PUT /v1/admin/accounts/:userId/analytics-exclusion` takes (§3.3).
 *
 * Listing takes a note, and refuses an empty one: a number that is smaller than what happened needs
 * its reason written down somewhere other than the head of whoever made it smaller. Unlisting needs
 * none — it puts the numbers back the way they were.
 */
export const SetAnalyticsExclusionRequest = z.discriminatedUnion('excluded', [
    z.object({ excluded: z.literal(true), note: z.string().trim().min(1).max(500) }),
    z.object({ excluded: z.literal(false) }),
]);
/** One of an account's teams, with what that account actually resolves to inside it. */
export const AdminAccountTeam = z.object({
    id: Ulid,
    slug: z.string(),
    name: z.string(),
    role: TeamRole,
    plan: Plan,
    playgroundSeats: z.number().int(),
    /** The enabled keys for this person in this team — the pair, resolved. */
    features: z.array(z.string()),
});
export const AdminAccountDetail = z.object({
    account: AdminAccount,
    /** Null when the account is counted like anybody else. */
    analyticsExclusion: AdminAnalyticsExclusion.nullable(),
    /** Every registry key, as this account's tier answers it. A paid key is `null`: it is the team's. */
    features: z.array(AdminFeatureState),
    teams: z.array(AdminAccountTeam),
});
export const AdminTeam = z.object({
    id: Ulid,
    slug: z.string(),
    name: z.string(),
    plan: Plan,
    playgroundSeats: z.number().int(),
    /** How big a machine this team's sandboxes get. The other half of `playgroundSeats`. */
    sandboxSize: SandboxSize,
    /** How big a machine this team's Claude Code sandboxes get. */
    claudeCodeSandboxSize: ClaudeCodeSandboxSize,
    /** How long one of this team's sandboxes may last, in minutes. */
    sandboxMinutes: z.number().int(),
    /**
     * One of Boardaux's own teams (`teams.first_party_at`, migration 0097): the only teams whose games
     * can have printed figures (docs/meshy-models-architecture.md §0.12). An operator's switch.
     */
    firstParty: z.boolean(),
    members: z.number().int(),
    games: z.number().int(),
    createdAt: z.number().int(),
});
/**
 * A team's roster as an admin sees it: handles, and nothing else.
 *
 * A member of a team sees their colleagues' real names and addresses, because
 * `apps/api/src/lib/identity.ts` says team membership is what earns that. An admin is not a member
 * of the teams they administer, and that module is not special-cased for them (§D7) — so this
 * carries the username, and the reveal route is how an admin gets past it, one person at a time
 * and with an audit row behind them.
 */
export const AdminTeamMember = z.object({
    userId: Ulid,
    username: z.string(),
    role: TeamRole,
    joinedAt: z.number().int(),
});
export const AdminTeamDetail = z.object({
    team: AdminTeam,
    /** Every registry key, as this team's plan answers it. A beta or alpha key is `null`: it is the person's. */
    features: z.array(AdminFeatureState),
    members: z.array(AdminTeamMember),
});
/**
 * One key: `FEATURES` on the wire, with the tier `feature_tiers` gives it.
 *
 * The registry is the only list of keys, so a row in `feature_tiers` for a key it does not know
 * gates nothing and is not shown; a key with no row is `off`.
 */
export const AdminFeatureDefinition = z.object({
    key: FeatureKey,
    /** Where the feature is in its release. `off` when `feature_tiers` has no row for it. */
    tier: FeatureTier,
    /** When an operator last moved it, or null for one never moved since migration 0088 seeded it. */
    updatedAt: z.number().int().nullable(),
    summary: z.string(),
    /** What having it does. See `FeatureDefinition.effect`. */
    effect: z.string(),
});
/**
 * How many each tier reaches, for the Features screen: who a feature would reach if it were moved
 * there, which is the question to answer before moving one.
 *
 * Each is a count of exactly that value — `beta` does not include the alpha, though a beta feature
 * reaches both — and leaves out disabled accounts, whose tier the resolver reads as none. Not
 * analytics: nobody's activity is counted, only who holds what, as an operator set it or the person
 * chose it.
 */
export const AdminFeatureCounts = z.object({
    /** Enabled accounts whose tier is `beta`. */
    beta: z.number().int(),
    /** Enabled accounts whose tier is `alpha`. */
    alpha: z.number().int(),
    /** Teams on the paid plan. */
    paidTeams: z.number().int(),
});
/** `GET /v1/admin/features`. */
export const AdminFeaturesResponse = z.object({
    items: z.array(AdminFeatureDefinition),
    counts: AdminFeatureCounts,
});
/* -------------------------------------------------------------------------- games (§D5.6) */
/**
 * One game, as the console sees it: which team owns it, what it publishes as, and how much is
 * already published under that name.
 *
 * `readyBuilds` is the field this row exists for. Renaming a package id changes what the *next*
 * build must declare and nothing about the ones already on a device — those install as the id
 * their bytes carry, and a Board that has one will not recognise the renamed game as the same
 * app. That is a fact about somebody else's devices, so the count travels with the row rather
 * than being something an admin is expected to remember to check.
 */
export const AdminApp = z.object({
    id: Ulid,
    teamId: Ulid,
    teamName: z.string(),
    teamSlug: z.string(),
    slug: z.string(),
    name: z.string(),
    platform: Platform,
    packageName: z.string(),
    archived: z.boolean(),
    /** Finished builds. Every one of them declares the package id it was finalized against. */
    readyBuilds: z.number().int(),
    /**
     * Where this game stands with the store (`docs/admin-plan.md` §D5.7).
     *
     * The console shows all three because the decision needs all three: what is missing is the rule
     * being waived, whether it is already published says whether waiving changes anything today, and
     * whether a build is sitting on `public` says whether publishing would put it on a shelf at all.
     * A waiver granted to a game with no build reaches nobody, and an operator should see that before
     * granting it rather than after being asked why the store is still empty.
     */
    store: z.object({
        publishedAt: z.number().int().nullable(),
        /**
         * When an operator approved this game for the store, or null (`docs/store-review-plan.md`).
         * Never cleared; every game on the store before review existed was approved by migration 0050.
         */
        approvedAt: z.number().int().nullable(),
        /** When the submission now waiting on an operator was made, or null if none is. */
        pendingSince: z.number().int().nullable(),
        /** Every checklist item actually absent — never suppressed by the waiver. */
        missing: z.array(z.string()),
        readinessWaivedAt: z.number().int().nullable(),
        /** Finished builds on the `public` channel: whether publishing would show anything. */
        publicBuilds: z.number().int(),
        /**
         * Whether a stranger browsing the store would see this game **right now**.
         *
         * Not `publishedAt !== null`, and the gap between the two is the whole reason this is on the
         * wire. The catalog also drops an archived game and a game with nothing installable on
         * `public`, so a team can publish successfully and reach nobody. Resolved by
         * `@boardflight/worker-core/catalog`, which is the same module the storefront's own query is
         * built from — one rule, so an operator's pill and a player's shelf cannot disagree.
         */
        visible: z.boolean(),
        /**
         * Builds on `public` the catalog would actually show — `publicBuilds` minus the ones held back
         * by the hardware-verification gate, where this deployment enforces it.
         *
         * Both counts travel because the difference is a different diagnosis: `publicBuilds` of 0 is
         * "nobody promoted anything", and `publicBuilds` of 3 with `visibleBuilds` of 0 is "the runner
         * has not got to them yet", which is waiting rather than a mistake.
         */
        visibleBuilds: z.number().int(),
    }),
    /**
     * What the store's ranking is computed from, **exactly** — this is not the storefront's
     * `rounded()` figure.
     *
     * A card rounds because publishing "137 plays" would tell every other studio how a stranger's
     * game is doing. This console is BoardFlight's own operators, who already read exact per-game
     * numbers on the Analytics tab; rounding here would only make the ranking unauditable.
     *
     * Present for every game, published or not: `store_stats` is rewritten by the cron for anything
     * with events, and the storefront filter is applied by the *catalog* queries rather than by the
     * aggregate. A private game with plays is a real row and not a leak — nothing here reaches a
     * client that is not an admin's.
     */
    stats: z.object({
        plays7d: z.number().int(),
        playsAll: z.number().int(),
        installs7d: z.number().int(),
        installsAll: z.number().int(),
    }),
    /** The listing's own filing, in the developer's order. Empty is "not declared". */
    genres: z.array(z.string()),
    createdAt: z.number().int(),
});
/**
 * A game, plus the prefixes its team owns — which is the whole of what decides a rename.
 *
 * The prefixes are on the detail rather than on the search row because they are what the console
 * must show *before* somebody types a new id: a rename that is not covered by one of them is
 * refused, and a form that only says so afterwards is a form that wastes the attempt. §4 is not
 * relaxed for an admin (`docs/admin-plan.md` §D5.6) — an operator who needs a game under a prefix
 * its team has not claimed claims the prefix first, as that team would have to.
 */
export const AdminAppDetail = z.object({
    app: AdminApp,
    prefixes: z.array(z.string()),
});
/* ------------------------------------------------- a game's builds (§D5, *A game's builds*) */
/**
 * One finished build, as the console lists it to download or preview.
 *
 * Every channel's, the team's own `playground` and `internal` included: that is the point of the
 * screen, and it is BoardFlight's operators reading it. No uploader, because a name is
 * `identity.ts`'s question and the console has no reason to ask it here.
 */
export const AdminBuild = z.object({
    id: Ulid,
    buildNumber: z.number().int().nullable(),
    versionName: z.string(),
    versionCode: z.number().int(),
    channel: z.object({ id: Ulid, slug: z.string(), name: z.string() }),
    size: z.number().int(),
    sha256: z.string(),
    /** The feedback layer patched it, so its developer's own bytes download separately. */
    patched: z.boolean(),
    /** The bytes are past retention: listed, and nothing to download. */
    expired: z.boolean(),
    readyAt: z.number().int().nullable(),
    createdAt: z.number().int(),
});
export const AdminBuildList = z.object({
    platform: Platform,
    /** Where a web build is previewed, or null when this deployment has no preview host. */
    previewOrigin: z.string().nullable(),
    items: z.array(AdminBuild),
    limit: z.number().int(),
    total: z.number().int(),
});
/* ------------------------------------------------------------- store review (§D5 row 11) */
/**
 * A game waiting for an operator's first approval (`docs/store-review-plan.md` §4) — everything the
 * decision is made in front of, in one card.
 *
 * Artwork travels as **media ids**, not URLs. The console's Worker binds no bucket (§D3) and does
 * not know where the public API lives; the page builds `https://api…/v1/media/:id` itself, which is
 * public by design — the id is the capability, exactly as it is on a store page.
 *
 * The submitter is a **username**: an operator shares no team with them (§D7).
 */
export const AdminStoreSubmission = z.object({
    reviewId: Ulid,
    submittedAt: z.number().int(),
    submitter: z.string().nullable(),
    app: AdminApp,
    tagline: z.string().nullable(),
    description: z.string().nullable(),
    playersMin: z.number().int().nullable(),
    playersMax: z.number().int().nullable(),
    iconMediaId: z.string().nullable(),
    screenshotMediaIds: z.array(z.string()),
    /**
     * The newest build on `public` a stranger would be served — what is in the operator's Board
     * library right now. Null when there is none yet, which is what the card says instead.
     */
    latestPublicBuild: z
        .object({
        versionName: z.string(),
        versionCode: z.number().int(),
        createdAt: z.number().int(),
        verifiedAt: z.number().int().nullable(),
    })
        .nullable(),
});
/** A decision already made, for the history under the queue. */
export const AdminStoreDecision = z.object({
    reviewId: Ulid,
    appId: Ulid,
    appName: z.string(),
    teamName: z.string(),
    submittedAt: z.number().int(),
    decision: z.enum(['approved', 'rejected', 'withdrawn']),
    decidedAt: z.number().int(),
    /** The operator's username, or the submitter's for a withdrawal. */
    decidedBy: z.string().nullable(),
    note: z.string().nullable(),
    /** Whether the team has been emailed yet — the public API's hourly sweep does it. */
    notified: z.boolean(),
});
export const AdminStoreReviews = z.object({
    pending: z.array(AdminStoreSubmission),
    decided: z.array(AdminStoreDecision),
});
/**
 * Approve a waiting submission, or send it back (`docs/store-review-plan.md` §0.7).
 *
 * The note goes to the team **verbatim** — on their dashboard and in the email — so it is required
 * for a rejection, where it is the whole of what the team has to act on, and optional for an
 * approval. It is never shown with the operator's name on it.
 */
export const DecideStoreReviewRequest = z
    .object({
    decision: z.enum(['approved', 'rejected']),
    note: z.string().trim().max(2000).optional(),
})
    .refine((body) => body.decision === 'approved' || (body.note ?? '').length > 0, {
    message: 'Say what needs to change — the team reads this note, and it is all they get.',
    path: ['note'],
});
/* --------------------------------------------------------------------- analytics (§3.2) */
/** How the all-app table is ordered. `name` is the only non-metric, for finding one game. */
export const AdminAnalyticsSort = z.enum(['sessions', 'playtime', 'boards', 'returning', 'installers', 'name']);
/**
 * One game's numbers across a range, for the table no per-app screen could produce.
 *
 * The per-app screens answer "how is *this* game doing"; this answers "is any of this being used,
 * and by whom" — which is the question an admin actually opens the console with, and the only one
 * that needs to see across every team at once.
 */
export const AdminAppAnalytics = z.object({
    appId: Ulid,
    name: z.string(),
    packageName: z.string(),
    platform: z.string(),
    archived: z.boolean(),
    teamId: Ulid,
    teamName: z.string(),
    teamSlug: z.string(),
    sessions: z.number().int(),
    playtimeMs: z.number().int(),
    /**
     * Real Boards a BoardFlight desktop app found the game on, whose time with it overlaps the range
     * (`board_apps`, off-board-plan §6) — zero for a game that is not `collecting`. Not the layer's
     * install ids, which on a web game are minted afresh at every launch, and not the addresses
     * plays are keyed on. A Board with no desktop app behind it is not known.
     */
    boards: z.number().int(),
    installsSeen: z.number().int(),
    uninstallsSeen: z.number().int(),
    /**
     * Of `boards`, the ones that still had the game on a later day than the day they first did —
     * **kept**, as of the Board's last sync. It used to mean *started it on two or more days*;
     * nothing ties a play to a Board any more (analytics-plan §10), so this is what a Board's row
     * can say.
     */
    returningBoards: z.number().int(),
    /** The newest session or play in the range — both reported by the game — or null. */
    lastPlayedAt: z.number().int().nullable(),
    /**
     * Sessions in the same number of days just before this range, for the trend: the range moved
     * back by exactly its length. When the range runs to today it is cut at the same time of day, so
     * today's part of a day is compared with the same part of a day, never with a whole one.
     */
    previousSessions: z.number().int(),
    /**
     * Distinct accounts whose BoardFlight desktop app installed a build of this game in the range.
     * Written from its job reports whatever the game's analytics switch says, so it is the one
     * column every game has. An update of a game already on the Board is not an install
     * (`install_events.kind`), and an install done by hand through the Board's own page is not in it.
     */
    installers: z.number().int(),
    /** Install attempts a Board refused in the range — the sign something shipped broken. */
    installFailures: z.number().int(),
    /** Whether any channel of this game is collecting. An empty row means two different things. */
    collecting: z.boolean(),
});
export const AdminAnalyticsResponse = z.object({
    items: z.array(AdminAppAnalytics),
    /**
     * How many games matched, which may exceed `items.length`.
     *
     * Reported rather than implied: a truncated table that says nothing reads as "this is
     * everything", and an admin deciding that nobody uses the product should not be looking at a
     * silent top-N.
     */
    total: z.number().int(),
    /** Across every matching game, not only the ones in `items`. */
    totals: z.object({
        sessions: z.number().int(),
        playtimeMs: z.number().int(),
        /** Distinct Boards across every game — one Board that holds five games is one, not five. */
        boards: z.number().int(),
        /** Distinct accounts that installed anything — one account with three games is one. */
        installers: z.number().int(),
        /** Install attempts a Board refused, over every matching game. */
        installFailures: z.number().int(),
        /**
         * `previousSessions` over every game, including those with nothing in this range — a game
         * that stopped being played is exactly what the trend is for.
         */
        previousSessions: z.number().int(),
    }),
    /**
     * How many accounts are on the analytics exclusion list (§3.3), and therefore in none of the
     * above. Said on the page, so an operator comparing this with a raw count knows why they differ.
     */
    excludedAccounts: z.number().int(),
    limit: z.number().int(),
    /** The platform the numbers were narrowed to, or null for every game. */
    platform: z.string().nullable(),
    from: z.string(),
    to: z.string(),
});
/**
 * One reason Boards refused installs: the Board's own message, verbatim, and how often it came back.
 * `message` is null for the failures a Board gave no words for, which are one reason too.
 */
export const AdminInstallFailureReason = z.object({
    message: z.string().nullable(),
    failures: z.number().int(),
    accounts: z.number().int(),
    games: z.number().int(),
    /** The game, when every failure with this message was one game's. */
    appId: Ulid.nullable(),
    appName: z.string().nullable(),
    lastAt: z.number().int(),
});
/** One install a Board refused. The account is its handle, as everywhere in the console (§D7). */
export const AdminInstallFailure = z.object({
    id: z.string(),
    at: z.number().int(),
    appId: Ulid,
    appName: z.string(),
    platform: z.string(),
    versionName: z.string().nullable(),
    versionCode: z.number().int().nullable(),
    buildNumber: z.number().int().nullable(),
    channelSlug: z.string().nullable(),
    /** `install` or `update`, the job's; null on a row from before it was written. */
    kind: z.string().nullable(),
    userId: Ulid,
    username: z.string(),
    message: z.string().nullable(),
});
/** `GET /v1/admin/installs/failures` — why installs failed in a range, grouped and one by one. */
export const AdminInstallFailuresResponse = z.object({
    /** Every failure in the range, not only the ones listed. */
    total: z.number().int(),
    reasons: z.array(AdminInstallFailureReason),
    /** The newest failures, at most a hundred. */
    items: z.array(AdminInstallFailure),
    from: z.string(),
    to: z.string(),
});
/** What `POST /v1/admin/accounts/:userId/reveal` costs to ask (§D7). */
export const RevealIdentityRequest = z.object({
    /**
     * Why. Required and non-empty: this is the one read in the system whose justification exists
     * nowhere but in the head of the person doing it, and a note that may be empty is a note that
     * is empty.
     */
    note: z.string().trim().min(1).max(500),
});
export const RevealedIdentity = z.object({
    userId: Ulid,
    username: z.string(),
    displayName: z.string().nullable(),
    email: z.string(),
});
/* ---------------------------------------------------------------------- sandboxes (§4.4) */
/**
 * What machine a session got, on both rows that describe one (migration 0034).
 *
 * Two fields rather than one, because they can disagree and the disagreement is the interesting
 * case: `sandboxSize` is the word an operator set on the team, `templateId` is the E2B image that
 * word resolved to on this deployment. A `…_LARGE` var pointing at the wrong template is visible
 * only in the pair.
 *
 * Both are null for a session that started before the columns existed. Rendered as an em dash and
 * never inferred from the team's *current* size — that guess is precisely what these columns
 * replace, since the size is read at start and the column an operator edits afterwards describes
 * the next sandbox rather than the one that ran.
 *
 * Spread into the two schemas below rather than nested, so a row is one flat object on the wire
 * and one flat cell on the screen.
 */
const SANDBOX_MACHINE_SHAPE = {
    /**
     * One of `SandboxSize`, or null. Not typed as the enum, for `endReason`'s reason: it comes out
     * of a column deliberately left without a CHECK, and a client that threw on an unfamiliar value
     * would break the day a sixth size shipped.
     */
    sandboxSize: z.string().nullable(),
    /** The E2B template id. An image name, not a credential — see migration 0034. */
    templateId: z.string().nullable(),
};
/**
 * A sandbox that is running now.
 *
 * What the database believes, which is deliberately not what E2B would say — the console has no
 * E2B credentials and asking both would show a disagreement it cannot resolve as though it were a
 * fact. The API's hourly cron is what reconciles the two.
 */
export const AdminSandbox = z.object({
    playgroundId: Ulid,
    appId: Ulid,
    /** Null when the game has been deleted since. The console renders the id it has. */
    appName: z.string().nullable(),
    teamId: Ulid,
    teamName: z.string().nullable(),
    teamSlug: z.string().nullable(),
    engine: z.string(),
    status: z.string(),
    sandboxId: z.string().nullable(),
    startedAt: z.number().int().nullable(),
    elapsedMs: z.number().int().nullable(),
    startedByUserId: Ulid.nullable(),
    /** A handle. An admin is not a member of this team; the reveal route is how a name comes out. */
    startedByUsername: z.string().nullable(),
    ...SANDBOX_MACHINE_SHAPE,
    /**
     * The playground says a sandbox is up and no usage row is open for it.
     *
     * Surfaced rather than smoothed over: it means a start died between the two writes, so a seat is
     * held by something nothing is measuring. Should always be false.
     */
    untracked: z.boolean(),
});
export const AdminSandboxHistoryEntry = z.object({
    id: Ulid,
    playgroundId: Ulid,
    appId: Ulid,
    appName: z.string().nullable(),
    teamId: Ulid,
    teamName: z.string().nullable(),
    sandboxId: z.string().nullable(),
    startedByUserId: Ulid.nullable(),
    startedByUsername: z.string().nullable(),
    ...SANDBOX_MACHINE_SHAPE,
    startedAt: z.number().int(),
    endedAt: z.number().int(),
    durationMs: z.number().int(),
    /**
     * One of `SandboxEndReason`, or null on a row written before the reasons existed. Not typed as
     * the enum: this comes out of a column with no CHECK, deliberately, and a client that threw on
     * an unfamiliar value would break on the next reason added.
     */
    endReason: z.string().nullable(),
    endDetail: z.string().nullable(),
});
/**
 * Ending a session an admin has decided to end.
 *
 * The note is required for the reason the reveal's is: `admin_kill` is the one end reason whose
 * *why* is recoverable from nothing else in the system.
 */
export const EndSandboxRequest = z.object({
    note: z.string().trim().min(1).max(500),
});
/* ------------------------------------------------------------------ hours, three ways (§4.4) */
/**
 * The two numbers every usage table reports, however it is grouped.
 *
 * `staleStarts` is counted apart from `sessions` rather than dropped, and its milliseconds are not
 * in `playtimeMs`: a `stale_row` held a seat, so it is part of what a quota was spent on, but
 * nobody used it and counting it as time would inflate every total. Dropping it silently would
 * hide that a quota was being spent on nothing at all.
 */
const USAGE_TOTALS_SHAPE = {
    sessions: z.number().int(),
    staleStarts: z.number().int(),
    /** Open sessions count up to now — which is what "how much has this used" means mid-session. */
    playtimeMs: z.number().int(),
};
/** Hours per team: the number a *seat grant* should be argued from. */
export const AdminSandboxTeamUsage = z.object({
    teamId: Ulid,
    teamName: z.string().nullable(),
    teamSlug: z.string().nullable(),
    playgroundSeats: z.number().int().nullable(),
    ...USAGE_TOTALS_SHAPE,
});
/**
 * Hours per person: which account is spending the compute.
 *
 * A handle, like every other list in this console — an admin is not a member of the team whose
 * hours these are (§D7), and the reveal route is how a name comes out, one person at a time.
 *
 * `userId` is nullable and so is `username`, for two different reasons that both render the same
 * way. `started_by` keeps `ON DELETE SET NULL`, so a deleted account stops being named and the
 * hours it used still count; and a `stale_row` written by a start that died can carry no actor at
 * all. Either way the row is real compute and appears, grouped under one unattributed line.
 */
export const AdminSandboxPersonUsage = z.object({
    userId: Ulid.nullable(),
    username: z.string().nullable(),
    /** How many distinct teams this person started a sandbox in over the range. */
    teams: z.number().int(),
    ...USAGE_TOTALS_SHAPE,
});
/**
 * Hours per game: which project is spending the compute.
 *
 * `appName` is nullable because `playground_sessions` deliberately has no foreign key on `app_id`
 * — this is a record of compute that outlives the game somebody deleted afterwards (§4.1), and a
 * row naming a game that is gone renders as the id it has.
 */
export const AdminSandboxGameUsage = z.object({
    appId: Ulid,
    appName: z.string().nullable(),
    teamId: Ulid,
    teamName: z.string().nullable(),
    ...USAGE_TOTALS_SHAPE,
});
/* ---------------------------------------------------------------------------- the pieces */
/**
 * One box, and how many Boards say they have it.
 *
 * `display` is resolved server-side through `pieceSetDisplay` rather than left to the console,
 * so a set reported by a client newer than this deployment renders as a prettified name instead
 * of a raw key — and the console does not need a second copy of the registry to draw a table.
 */
export const AdminPieceSetCount = z.object({
    key: z.string(),
    display: z.string(),
    /** Boards that named this set in their most recent report. */
    boards: z.number().int(),
    /** False for a key this build's `PIECE_MODELS` has never heard of — a newer client's box. */
    known: z.boolean(),
    /**
     * Games in the library whose primary model needs this set — what is asked of a Board, beside what
     * Boards have. Only on the Pieces screen; the onboarding's tallies leave it out.
     */
    games: z.number().int().optional(),
});
/**
 * What players own, as far as BoardFlight can honestly tell (`docs/model-intro-plan.md` P1).
 *
 * **The denominators are part of the answer, not decoration.** The inventory lives on the device
 * and is mirrored here one way, so this is what BoardFlight's own client saw on Boards that paired
 * *and* signed in — the same caveat `docs/store-plan.md` §5.1 puts beside the store stats. A
 * percentage over `answered` is a real number; a percentage over the world is not, and the console
 * shows both figures so nobody has to guess which one they are reading.
 *
 * `answered` and `empty` are the two states the whole design keeps apart: a Board that has never
 * been asked is not a Board that owns nothing, and reporting them as one number would throw away
 * the distinction the client is careful to preserve.
 */
export const AdminPiecesSummary = z.object({
    /** Boards BoardFlight knows about at all — every `devices` row. The honest denominator. */
    boards: z.number().int(),
    /** Boards that have answered the question. Everything below is out of this. */
    answered: z.number().int(),
    /** Of those, the ones that answered "none of them". */
    empty: z.number().int(),
    /** Games in the library at all, the denominator of each set's `games`. */
    libraryGames: z.number().int(),
    /** Every set in the registry, plus any a newer client reported. Zero counts included. */
    sets: z.array(AdminPieceSetCount),
});
/**
 * The onboarding, as a funnel — one per place it runs (`docs/off-board-plan.md` §5.6).
 *
 * Every count is **installs**, not events: a household that looked at the piece step three times
 * reached it once. The unit is the reporter's own install id — per browser profile on the website,
 * per desktop install in the app — so a reinstall starts a new row, which is the honest reading of
 * somebody starting over.
 *
 * The first count is the denominator, and it is the installs whose onboarding *first* started
 * inside the range. A household that started in March and finished in April is counted in March's
 * funnel; that is what makes the stages a funnel rather than unrelated tallies. One that started in
 * March and opened the onboarding again in April is still March's, and in no April funnel at all —
 * otherwise April would count, as April's, everything it did in March.
 *
 * One shape for all three groups. A count a group has no step for is simply zero there — the
 * website never pairs, the Board client never asked about running in the background — and the
 * console draws each group against its own path (`onboardingStepsOf`), so a zero for a step that
 * group never had is not drawn as a drop.
 */
export const AdminOnboardingFunnel = z.object({
    /** Opened the onboarding. The denominator. */
    started: z.number().int(),
    /** Reached each step, by the step's own report. */
    pieces: z.number().int(),
    pair: z.number().int(),
    account: z.number().int(),
    done: z.number().int(),
    /** Off the Board only: the website's *Get the app* and *Waiting*, the app's *Find* and *Background*. */
    getApp: z.number().int(),
    waiting: z.number().int(),
    find: z.number().int(),
    background: z.number().int(),
    /** The phone's app only: the step that asks about notifications from BoardFlight. */
    notifications: z.number().int(),
    /** Answered the piece question. */
    piecesConfirmed: z.number().int(),
    /** …with exactly the sets a regular Board comes with, untouched. */
    piecesDefaultsKept: z.number().int(),
    /** Pressed Pair this Board at least once, so Board's approval prompt went up. */
    pairRequested: z.number().int(),
    /** Paired the Board during the onboarding. */
    paired: z.number().int(),
    /** Pressed Skip on the pair step. */
    pairSkipped: z.number().int(),
    /** Board refused or could not be reached at least once. */
    pairFailed: z.number().int(),
    /** Saw a sign-in code on the account step. */
    codeShown: z.number().int(),
    /** Signed in during the onboarding. */
    linked: z.number().int(),
    /** …with an account created after the onboarding started — a sign-up rather than a sign-in. */
    linkedNewAccount: z.number().int(),
    /** Pressed Skip on the account step. */
    skipped: z.number().int(),
    /** A code was declined, expired or never arrived at least once. */
    failed: z.number().int(),
    /** Website: pressed the download for their system. */
    appDownloadClicked: z.number().int(),
    /** Website: a Board appeared on the account while the page was watching for one. */
    handoffLinked: z.number().int(),
    /** Desktop app: opened the onboarding for the first time on that computer. */
    appOpened: z.number().int(),
    /** Desktop app: a search found a Board at least once. */
    boardFound: z.number().int(),
    /** Desktop app: a search ended with nothing at least once. */
    boardNotFound: z.number().int(),
    /** Desktop app: answered "Keep BoardFlight running". */
    backgroundChosen: z.number().int(),
    /** …with yes, in their latest answer. */
    backgroundEnabled: z.number().int(),
    /** Reached the library through the last step. */
    completed: z.number().int(),
    /** Left the onboarding without finishing it. */
    exited: z.number().int(),
});
/** `?days=` — how far back the cohort's starts reach. A month unless asked; never past retention. */
export const AdminOnboardingQuery = z.object({
    days: z.coerce.number().int().min(1).max(400).default(30),
});
/** One word from a closed list an event carries, and how many installs reported it. */
export const AdminOnboardingTally = z.object({ key: z.string(), installs: z.number().int() });
/** One group's onboarding: a surface's, or the retired Board client's. */
export const AdminOnboardingGroup = z.object({
    /** `desktop`, `web`, or `board` for rows that name no surface (`CLIENT_EVENT_SOURCES`). */
    source: z.enum(CLIENT_EVENT_SOURCES),
    funnel: AdminOnboardingFunnel,
    /** Median seconds from start to the last step, over the installs that completed. Null for none. */
    medianSeconds: z.number().nullable(),
    /**
     * What the piece step was answered with: each set, and how many installs ticked it in their
     * latest answer. Out of `funnel.piecesConfirmed`. Every registry set is listed, zero included.
     */
    sets: z.array(AdminPieceSetCount),
    /** Started, per release — the split that says whether a new build moved the funnel. */
    versions: z.array(z.object({
        clientVersion: z.string().nullable(),
        started: z.number().int(),
        completed: z.number().int(),
    })),
    /** How Boards were found (`mdns`, `sweep`, `remembered`, `typed`). Out of `funnel.boardFound`. */
    foundBy: z.array(AdminOnboardingTally),
    /** Why pairings failed (`refused`, `timeout`, `unreachable`, `error`). Out of `funnel.pairFailed`. */
    pairFailures: z.array(AdminOnboardingTally),
});
export const AdminOnboardingSummary = z.object({
    from: z.number().int(),
    to: z.number().int(),
    /** Always all three, in `CLIENT_EVENT_SOURCES` order, a group nobody started included. */
    groups: z.array(AdminOnboardingGroup),
});
/**
 * `GET /v1/admin/events/ingest` — custom game events per game over a range, from
 * `event_ingest_daily` (docs/game-events-plan.md §9.5): the one cross-game number BoardFlight keeps
 * outside the games' own stores. Counts only.
 */
export const AdminEventIngestRow = z.object({
    appId: Ulid,
    appName: z.string(),
    teamName: z.string(),
    accepted: z.number().int(),
    droppedCap: z.number().int(),
    droppedInvalid: z.number().int(),
    /** Thrown away on the device because its queue was full, as the layers report it. */
    droppedDevice: z.number().int(),
    undeclared: z.number().int(),
    mismatched: z.number().int(),
    lastAt: z.number().int().nullable(),
});
export const AdminEventIngestResponse = z.object({
    from: z.string(),
    to: z.string(),
    items: z.array(AdminEventIngestRow),
    /** Over every game that sent anything in the range, not only the rows listed. */
    totals: z.object({
        accepted: z.number().int(),
        /** Dropped by the server: over the daily cap, or malformed. */
        dropped: z.number().int(),
        /** Thrown away on the devices, which reported only how many. Kept apart from `dropped`. */
        droppedDevice: z.number().int(),
        games: z.number().int(),
    }),
});
/* -------------------------------------------------------------------------- releases */
/*
 * The release gate's shapes that fit any released thing: one run of the release workflow, and the
 * request that starts one. The gate releases the desktop app now (`docs/client-release-plan.md`),
 * and the answer its console tab reads is `apps/admin/src/release-types.ts`.
 */
/** One run of `client-promote.yml`, as GitHub reports it. */
export const AdminReleaseRun = z.object({
    /** From the run's name, "Release client 0.1.58". Null if the name ever stops saying. */
    version: z.string().nullable(),
    status: z.string(),
    /** `success`, `failure`, `cancelled`… — null while it runs. */
    conclusion: z.string().nullable(),
    url: z.string(),
    createdAt: z.string(),
    actor: z.string().nullable(),
});
/** `POST /v1/admin/client-releases/:version/promote`. */
export const PromoteClientRequest = z.object({
    /** Allow a version older than the released one. Changes fresh installs only. */
    rollback: z.boolean().optional(),
});
export const PromoteClientResponse = z.object({
    dispatched: z.literal(true),
    version: z.string(),
    /** The run, when GitHub said which one it started. */
    runUrl: z.string().nullable(),
});
/* ------------------------------------------------------------- one account's activity (§3.4) */
/**
 * Where an activity row came from, which is the Activity panel's one filter: a Board (installs,
 * sign-ins, sessions, feedback sent from a game), the store, or the dashboard and its team.
 */
export const ADMIN_ACTIVITY_GROUPS = ['board', 'store', 'dashboard'];
export const AdminActivityGroup = z.enum(ADMIN_ACTIVITY_GROUPS);
export const AdminActivityKind = z.enum([
    // board
    'board_sign_in',
    'install',
    'presence',
    'feedback',
    /** Play pressed in the desktop app's window, and the Board said yes. Not a play. */
    'launch',
    'client_event',
    // store
    'store_view',
    'library_add',
    'code_redeemed',
    'access_granted',
    // dashboard
    'signup',
    'sign_in',
    'team_joined',
    'playground',
    'team_action',
]);
/**
 * One thing an account did, from a row that names the account itself.
 *
 * Never from a row reached through an install id or a device hash, and never a play: plays are not
 * tied to accounts (`docs/analytics-plan.md` §10, 2026-10-21), and joining one to presence to make
 * it somebody's is the de-anonymization §10 forbids, for an operator too.
 */
export const AdminActivityItem = z.object({
    /** `<kind>:<row id>` — stable across pages, and the cursor's tie-break. */
    key: z.string(),
    at: z.number().int(),
    kind: AdminActivityKind,
    /** The game it was about, when it was about one. A deleted game keeps its id and loses its name. */
    appId: z.string().nullable(),
    appName: z.string().nullable(),
    /** The team the game or the action belongs to. */
    teamName: z.string().nullable(),
    /** Which of the account's Boards, by the id `boards` lists. */
    deviceId: z.string().nullable(),
    /** One line: a status and version, an action name, a channel, a client version. Never a person. */
    detail: z.string().nullable(),
    /** A play session's or a sandbox's length. Null while it is still running, and for everything else. */
    durationMs: z.number().int().nullable(),
    /** A sign-in's newest refresh. Null until it has had one, and for everything else. */
    lastUsedAt: z.number().int().nullable(),
});
/**
 * One of this account's Boards, as its BoardFlight desktop app synced it — or, with nothing on it,
 * one only the retired client ever reported. Unnamed, always: a Board's name is its owner's.
 */
export const AdminActivityBoard = z.object({
    /** The id its events carry: the client's `devices` row where there was one, else the Board's. */
    id: z.string(),
    firstSeenAt: z.number().int(),
    /** When a desktop app last read it. */
    lastSeenAt: z.number().int().nullable(),
    /** Games the last sync found on it, among the games that have analytics on. */
    installed: z.number().int(),
});
/** A game on one of the account's Boards, as its desktop app's last sync left it. Measured games only. */
export const AdminActivityInstalled = z.object({
    appId: z.string(),
    appName: z.string().nullable(),
    teamName: z.string().nullable(),
    deviceId: z.string(),
    versionName: z.string().nullable(),
    firstSeenAt: z.number().int(),
    lastSeenAt: z.number().int(),
    uninstalledAt: z.number().int().nullable(),
});
/** `GET /v1/admin/accounts/:userId/activity?group=&before=` (§3.4). */
export const AdminAccountActivity = z.object({
    boards: z.array(AdminActivityBoard),
    games: z.array(AdminActivityInstalled),
    /** Newest first. */
    items: z.array(AdminActivityItem),
    /** Pass back as `?before=` for the page after this one. Null when this page reached the start. */
    next: z.string().nullable(),
    limit: z.number().int(),
    /** How many days each pruned source keeps, so an empty stretch is read as a window and not as idleness. */
    retentionDays: z.object({
        storeEvents: z.number().int(),
        analytics: z.number().int(),
        clientEvents: z.number().int(),
    }),
});
/* ------------------------------------------------------------------ active accounts (§3.5) */
/**
 * How long a day of use is kept — `account_days` and `board_days` (migration 0082). The same window
 * as a play session's: a row that says somebody used BoardFlight on a given day is a fact about a
 * person's day, and it goes when the analytics rows about one do (`ANALYTICS_RETENTION_DAYS`).
 */
export const ACTIVITY_DAY_RETENTION_DAYS = 180;
/** Distinct accounts or Boards over three trailing windows. */
export const AdminActiveWindows = z.object({
    /** The last 24 hours, or one UTC day. */
    day: z.number().int(),
    /** The last 7 days. */
    week: z.number().int(),
    /** The last 30 days. */
    month: z.number().int(),
});
/**
 * One UTC day's DAU, WAU and MAU — distinct over that day, the 7 days and the 30 days ending with it.
 * Null where the window reaches back before the first day held (`since`): a count that silently
 * covers part of its window reads as a fall that never happened.
 */
export const AdminActiveCounts = z.object({
    daily: z.number().int().nullable(),
    weekly: z.number().int().nullable(),
    monthly: z.number().int().nullable(),
});
export const AdminActiveDay = z.object({
    /** `YYYY-MM-DD`, UTC. The last one is today, so far. */
    day: z.string(),
    /** Accounts that signed in or refreshed a session, of the asked-for kind if one was asked for. */
    accounts: AdminActiveCounts,
    /** Boards a desktop app reached — switched on and on its network — counted once however many accounts connected one. */
    boards: AdminActiveCounts,
    /** Game sessions started. Not people: no session names an account or a Board (analytics-plan §10). */
    sessions: z.number().int(),
    /** How long those sessions ran, together: to their end, or to their last sign of life while open. */
    playtimeMs: z.number().int(),
    /** Games with at least one session that day. */
    games: z.number().int(),
});
/**
 * Play over one trailing window. Sessions and games, never people: there is no per-player or
 * per-Board figure because no play names one (analytics-plan §10).
 */
export const AdminActivePlay = z.object({
    /** Game sessions started in the window. */
    sessions: z.number().int(),
    /** Their length, together. */
    playtimeMs: z.number().int(),
    /** Games with at least one of them. */
    games: z.number().int(),
    /** The middle session's length; null with no session. A median, because a Board left on with a game open files a session of hours. */
    medianSessionMs: z.number().int().nullable(),
});
/** `GET /v1/admin/active?days=&kind=` (§3.5). Excluded accounts (§3.3) are in none of it. */
export const AdminActiveResponse = z.object({
    /** Exact, as of the request, from when each session and Board was last seen — whatever `since` says. */
    now: z.object({
        accounts: AdminActiveWindows,
        /** The same, per session kind. An account using two is in both rows, so they do not add up. */
        byKind: z.array(AdminActiveWindows.extend({ kind: SessionKind })),
        boards: AdminActiveWindows,
        /** Play over the same three windows. Not narrowed by `kind`: a session names no account. */
        play: z.object({ day: AdminActivePlay, week: AdminActivePlay, month: AdminActivePlay }),
    }),
    /** The first day a day of use was recorded, or that retention still keeps. Null before any was. */
    since: z.string().nullable(),
    /** The kind the account counts were narrowed to, or null for every kind. */
    kind: SessionKind.nullable(),
    /** Oldest first, ending today. */
    days: z.array(AdminActiveDay),
    retentionDays: z.number().int(),
});
//# sourceMappingURL=admin.js.map